import { useCallback, useEffect, useState } from "react";

import { PlaceholderEditor } from "@fronterahealth/frontera-ui-components";

import {
  RecommendationBankItemType,
  UpdateRecommendationBankItemInput,
  useUpdateRecommendationBankItemMutation,
} from "@api/graphql/types-and-hooks";
import { PlaceholderValues, RecommendationPlaceholderEditorVariant } from "@components/GoalsBank/constants";
import { renderRecommendationBankFormDialog } from "@components/RecommendationsBank/utils";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifySuccess } from "@components/notifications/notifications";

interface RecommendationsEditFormProps {
  refetchRecommendations: () => void;
  selectedRecommendation: RecommendationBankItemType;
  openEditForm: boolean;
  setOpenEditForm: React.Dispatch<React.SetStateAction<boolean>>;
}

export const RecommendationsEditForm: React.FC<RecommendationsEditFormProps> = ({
  refetchRecommendations,
  selectedRecommendation,
  openEditForm,
  setOpenEditForm,
}) => {
  const updateRecommendation = useUpdateRecommendationBankItemMutation({});
  const [content, setContent] = useState(selectedRecommendation?.recommendationContent);
  const handleSubmit = useCallback(
    async (params: UpdateRecommendationBankItemInput) => {
      try {
        await updateRecommendation.mutateAsync({
          input: {
            folderId: selectedRecommendation?.folder?.id,
            recommendationBankId: selectedRecommendation.id,
            recommendationTitle: params?.recommendationTitle,
            recommendationContent: content ?? "",
          },
        });
        refetchRecommendations();
        notifySuccess("Successfully Updated Recommendation");
        setOpenEditForm(false);
      } catch (error) {
        console.error("Error when saving Recommendation", error);
      }
    },
    [updateRecommendation, refetchRecommendations, selectedRecommendation, setOpenEditForm],
  );

  const { formState, RegisteredFormInput, reset, onSubmit } = useFormUtils<UpdateRecommendationBankItemInput>({
    mutationFn: handleSubmit,
  });

  useEffect(() => {
    if (selectedRecommendation) {
      reset({
        recommendationTitle: selectedRecommendation.recommendationTitle ?? "",
        recommendationContent: selectedRecommendation.recommendationContent ?? "",
      });
    }
  }, [reset, selectedRecommendation]);

  const dialog = renderRecommendationBankFormDialog({
    title: "Editing Recommendation",
    openForm: openEditForm,
    setOpenForm: setOpenEditForm,
    onSubmit: async () => {
      await onSubmit();
    },
    children: (
      <FormContainer onSubmit={onSubmit}>
        <RegisteredFormInput
          formKey="recommendationTitle"
          formState={formState}
          label="Recommendation Name"
          required
          className="pr-8"
        />
        <PlaceholderEditor
          key={selectedRecommendation.id}
          title="Content"
          className="mb-6 mr-2"
          existingContent={selectedRecommendation.recommendationContent ?? ""}
          placeholder="Type / for dynamic content"
          variant={RecommendationPlaceholderEditorVariant}
          onChange={(value) => setContent(value)}
          insertValues={PlaceholderValues}
        />
      </FormContainer>
    ),
  });

  return <>{dialog}</>;
};
